import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/* Canon .btn of the prototypes: 12.5px text, ~26px high, 4px corners, hairline border.
   variant — default = primary (the accent), secondary/outline = the plain bordered one, ok = confirm,
   destructive = solid red, danger = quiet red outline, text = the "ghost" text link (＋ add, Open, Edit),
   ghost = icon-ish, no chrome until hover. size icon = the 28x26 .icon square. */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded text-ui transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "border border-hl bg-hl font-semibold text-hl-ink hover:bg-hl/90",
        ok: "border border-ok bg-ok font-semibold text-ok-ink hover:bg-ok/90",
        destructive:
          "border border-destructive bg-destructive font-semibold text-destructive-foreground hover:bg-destructive/90",
        danger:
          "border border-danger/40 bg-transparent text-danger hover:bg-danger/10",
        outline:
          "border border-line bg-panel hover:border-muted-foreground",
        secondary:
          "border border-line bg-panel hover:border-muted-foreground",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        text: "border border-transparent bg-transparent px-0.5 text-hl hover:underline",
        textDanger: "border border-transparent bg-transparent px-0.5 text-danger hover:underline",
        link: "text-hl underline-offset-4 hover:underline",
        /* `.btn.danger` of the approved confirmation: the one red button, only inside «Удалить …?» (П5). */
        dangerSolid: "border border-danger bg-danger font-semibold text-danger-ink hover:bg-danger/90",
        /* `.ib` of the sidebar mockup: an icon with no chrome until hover / focus / open (П3). */
        quiet: "border border-transparent bg-transparent text-muted-foreground hover:bg-panel-2 hover:text-fg aria-expanded:bg-panel-2 aria-expanded:text-fg",
      },
      size: {
        default: "h-7 px-[9px] pointer-coarse:h-9",
        sm: "h-[26px] px-[9px] pointer-coarse:h-9",
        lg: "h-8 px-4 text-body-s",
        icon: "h-[26px] w-7 px-0 pointer-coarse:h-9 pointer-coarse:w-9",
        inline: "h-auto",
        /* the row-size icon (24x22): fits the 28 px list row (П2) */
        iconSm: "h-[22px] w-6 px-0 pointer-coarse:h-8 pointer-coarse:w-8",
        /* a 32 x 32 swatch (the token presets of the Map tab) */
        swatch: "h-[32px] w-[32px] px-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const variants = buttonVariants;
    return (
      <Comp
        className={cn(variants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
